/**
 * Admin Failed Transactions Page
 *
 * Dead letter queue viewer for transactions that failed processing
 * in the ingestion service. Shows stats, filters, and expandable rows
 * with raw transaction/error details.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

// ==================== API ====================

const API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

async function fetchWithAuth(endpoint: string) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ==================== Types ====================

interface Stats {
  total: number;
  unresolved: number;
  resolved: number;
  last24h: number;
  byProcessor: { processor: string; total: number; unresolved: number }[];
}

interface FailedTransaction {
  id: string;
  transaction_hash: string | null;
  transaction_type: string | null;
  account: string | null;
  ledger_index: number | null;
  processor: string;
  error_message: string;
  error_stack: string | null;
  raw_transaction: any;
  raw_metadata: any;
  retry_count: number;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

// ==================== Helpers ====================

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function truncateHash(hash: string | null): string {
  if (!hash) return '-';
  return `${hash.slice(0, 8)}...${hash.slice(-6)}`;
}

function truncateAddress(address: string | null): string {
  if (!address) return '-';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

const PROCESSOR_COLORS: Record<string, string> = {
  nft: 'bg-purple-100 text-purple-800',
  payment: 'bg-blue-100 text-blue-800',
  xspectar_mint: 'bg-green-100 text-green-800',
  account_set: 'bg-orange-100 text-orange-800',
};

function ProcessorBadge({ processor }: { processor: string }) {
  const color = PROCESSOR_COLORS[processor] || 'bg-gray-100 text-gray-800';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {processor}
    </span>
  );
}

const PROCESSORS = ['All', 'nft', 'payment', 'xspectar_mint', 'account_set'];

// ==================== Component ====================

export function AdminFailedTransactionsPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [rows, setRows] = useState<FailedTransaction[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filters
  const [processorFilter, setProcessorFilter] = useState('All');
  const [resolvedFilter, setResolvedFilter] = useState<string>('false');
  const [page, setPage] = useState(1);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/admin/failed-transactions/stats');
      setStats(res.data);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', '50');
      if (processorFilter !== 'All') params.append('processor', processorFilter);
      if (resolvedFilter !== 'all') params.append('resolved', resolvedFilter);

      const res = await fetchWithAuth(`/admin/failed-transactions?${params}`);
      setRows(res.data.failedTransactions);
      setPagination(res.data.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load failed transactions');
    } finally {
      setLoading(false);
    }
  }, [page, processorFilter, resolvedFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRows(); }, [loadRows]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Failed Transactions</h1>
          <button
            onClick={() => { loadStats(); loadRows(); }}
            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Failed" value={stats.total} />
            <StatCard label="Unresolved" value={stats.unresolved} highlight={stats.unresolved > 0} />
            <StatCard label="Resolved" value={stats.resolved} />
            <StatCard label="Last 24h" value={stats.last24h} highlight={stats.last24h > 0} />
          </div>
        )}

        {/* Processor breakdown */}
        {stats && stats.byProcessor.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">By Processor</h3>
            <div className="flex flex-wrap gap-4">
              {stats.byProcessor.map((p) => (
                <div key={p.processor} className="flex items-center gap-2">
                  <ProcessorBadge processor={p.processor} />
                  <span className="text-sm text-gray-600">{p.total} total, {p.unresolved} unresolved</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <ErrorDisplay error={error} />}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            {PROCESSORS.map((p) => (
              <button
                key={p}
                onClick={() => { setProcessorFilter(p); setPage(1); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full ${
                  processorFilter === p
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {p === 'All' ? 'All' : p}
              </button>
            ))}
          </div>
          <select
            value={resolvedFilter}
            onChange={(e) => { setResolvedFilter(e.target.value); setPage(1); }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
          >
            <option value="false">Unresolved</option>
            <option value="true">Resolved</option>
            <option value="all">All</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">TX Hash</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ledger</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Processor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No failed transactions found</td></tr>
                ) : (
                  rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(row.created_at)}</td>
                        <td className="px-4 py-3 text-sm">
                          {row.transaction_hash ? (
                            <a
                              href={`https://bithomp.com/explorer/${row.transaction_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 font-mono"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {truncateHash(row.transaction_hash)}
                            </a>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{row.transaction_type || '-'}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-700">{truncateAddress(row.account)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{row.ledger_index?.toLocaleString() || '-'}</td>
                        <td className="px-4 py-3"><ProcessorBadge processor={row.processor} /></td>
                        <td className="px-4 py-3 text-sm text-red-600 max-w-xs truncate" title={row.error_message}>
                          {row.error_message}
                        </td>
                      </tr>
                      {expandedId === row.id && (
                        <tr>
                          <td colSpan={7} className="px-4 py-4 bg-gray-50">
                            <div className="space-y-3">
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Error Message</h4>
                                <pre className="text-sm text-red-700 whitespace-pre-wrap bg-red-50 p-3 rounded border border-red-100">
                                  {row.error_message}
                                </pre>
                              </div>
                              {row.error_stack && (
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Stack Trace</h4>
                                  <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-100 p-3 rounded border max-h-40 overflow-y-auto">
                                    {row.error_stack}
                                  </pre>
                                </div>
                              )}
                              {row.raw_transaction && (
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Raw Transaction</h4>
                                  <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-100 p-3 rounded border max-h-60 overflow-y-auto">
                                    {JSON.stringify(row.raw_transaction, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {row.raw_metadata && (
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Raw Metadata</h4>
                                  <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-100 p-3 rounded border max-h-60 overflow-y-auto">
                                    {JSON.stringify(row.raw_metadata, null, 2)}
                                  </pre>
                                </div>
                              )}
                              <div className="flex gap-4 text-xs text-gray-500">
                                <span>Retry count: {row.retry_count}</span>
                                <span>Resolved: {row.resolved_at ? formatDate(row.resolved_at) : 'No'}</span>
                                <span>ID: {row.id}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <span className="text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} total)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                  disabled={page === pagination.totalPages}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

// ==================== Stat Card ====================

function StatCard({ label, value, highlight }: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${highlight ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
}
